import { useEffect, useRef, useState } from 'react'

import {
    Statsig,
    isStatsigClientInitialized,
    checkIfExperimentFailedOnPrevPage,
    setFailedExperimentInSessionStorage
} from '@/utils/statsig'

/**
 * Custom hook to fetch and manage statsig experiment variant in React components.
 * @param maximumAttempts - Maximum number of attempts to fetch experiment data. Default is 4.
 * @param interval - Interval (in milliseconds) between each attempt. Default is 100ms.
 * @param experimentName - Name of the statsig experiment.
 * @param experimentParameter - Parameter for the experiment variant.
 * @param defaultVariant - Default variant value if experiment fetch fails.
 */
const useStatsigExperiment = ({
    maximumAttempts = 4,
    interval = 100,
    experimentName = '',
    experimentParameter = '',
    defaultVariant = ''
}) => {
    const [loading, setLoading] = useState(true)
    const [variant, setVariant] = useState<string | null>(null)
    const [experimentFailedOnPrevPage, setExperimentFailedOnPrevPage] = useState(false)
    const [failedToFetchWithinCutoff, setfailedToFetchWithinCutoff] = useState(false)
    const attempts = useRef(0)
    const intervalRef = useRef<ReturnType<typeof setInterval>>()

    useEffect(() => {
        if (checkIfExperimentFailedOnPrevPage(experimentName)) {
            setLoading(false)
            setExperimentFailedOnPrevPage(true)
            return
        }
        intervalRef.current = setInterval(() => {
            if (failedToFetchWithinCutoff) return
            try {
                if (isStatsigClientInitialized()) {
                    setLoading(false)
                    const statsigExperiment = Statsig.getExperiment(experimentName)
                    setVariant(statsigExperiment.get(experimentParameter, defaultVariant))
                    if (intervalRef.current) clearInterval(intervalRef.current)
                    return
                }
                attempts.current++
                if (attempts.current > maximumAttempts) {
                    setFailedExperimentInSessionStorage(experimentName)
                    setfailedToFetchWithinCutoff(true)
                    if (intervalRef.current) clearInterval(intervalRef.current)
                }
            } catch (e) {
                console.log(e)
            }
        }, interval)
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [maximumAttempts, interval, experimentName, experimentParameter, defaultVariant])

    return { loading, variant, failedToFetchWithinCutoff, experimentFailedOnPrevPage }
}

export default useStatsigExperiment
